/**
 * Mounting the shared tenancy hints for this app.
 *
 * `ui-core`'s pills and preferences panel read three things from context: the
 * caller's saved defaults, this app's policy table, and where to send someone who
 * wants to change them. All three are the dashboard's to supply — the store is
 * the saved-views blob, the table is `@/config`, and the destination is this
 * app's settings route — which is exactly why the components take them rather
 * than importing them.
 *
 * Mounted under `DashboardPrefsProvider` (the store it adapts) and inside the
 * router, since `useNavigate` needs one. A surface rendered outside this still
 * draws correct pills; it just cannot offer the link.
 */
import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { ScopeHintProvider } from "@authe/ui-core";
import { OVERLAY_CONFIG, ROUTES } from "@/config";
import { useScopePrefs } from "./scopePrefs";

export function DashboardScopeHints({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const navigate = useNavigate();
  const prefs = useScopePrefs();
  const onConfigure = React.useCallback(
    () => void navigate({ to: ROUTES.settings }),
    [navigate],
  );
  return (
    <ScopeHintProvider prefs={prefs} policies={OVERLAY_CONFIG} onConfigure={onConfigure}>
      {children}
    </ScopeHintProvider>
  );
}
